/**
 * Client for engine /v1/jobs (assembly_quote_v1) + uploads (Slice C).
 */
import { handleUnauthorizedSession } from "./authSession";
import { ENGINE_BASE, engineAuthHeaders, engineFetch } from "./config";
import { logger } from "./logger";
import type {
  JobCreateRequest,
  JobParseRequest,
  JobRecord,
  PresignUploadResponse,
  UploadRecord,
} from "./jobTypes";

export type { JobRecord, PresignUploadResponse, UploadRecord } from "./jobTypes";

/**
 * True when the browser can PUT this presign URL.
 *
 * Engine signs against S3_ENDPOINT_URL, which on compose is `http://minio:9000`.
 * That host does not resolve in the browser, and HTTP from an HTTPS page is
 * mixed content. Those PUTs must go through the engine (multipart) instead.
 */
export function presignUsableInBrowser(
  url: string,
  pageProtocol: string = typeof window !== "undefined" ? window.location.protocol : "http:",
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
  const host = parsed.hostname.toLowerCase();
  if (host === "minio" || host.endsWith(".internal")) return false;
  if (pageProtocol === "https:" && parsed.protocol !== "https:") return false;
  return true;
}

function baseUrl(): string {
  return (
    (typeof window !== "undefined" && (window as { __ENGINE_URL__?: string }).__ENGINE_URL__) ||
    ENGINE_BASE
  );
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await engineFetch(`${baseUrl()}${path}`, init);
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    throw new Error(`jobApi ${path} ${res.status}: ${JSON.stringify(detail)}`);
  }
  return res.json() as Promise<T>;
}

/** Share of the batch's bytes that has left the browser, 0..1. */
export type UploadProgress = (fraction: number) => void;

/** CAD upload: presign PUT to MinIO, then complete. */
export async function uploadCadFiles(files: File[], onProgress?: UploadProgress): Promise<UploadRecord[]> {
  return uploadFiles(files, onProgress);
}

/** Mixed RFQ intake files (CAD + Excel/text/PDF/image). */
export async function uploadMixedFiles(files: File[], onProgress?: UploadProgress): Promise<UploadRecord[]> {
  return uploadFiles(files, onProgress);
}

type SentResponse = { ok: boolean; status: number; statusText: string; text: string };

/**
 * fetch() reports no upload progress, so a send that needs it goes through
 * XHR. Resolves with the status and body text; rejects only when the request
 * never reached the server.
 */
function sendWithProgress(
  method: string,
  url: string,
  body: Blob | FormData,
  headers: Record<string, string>,
  withCredentials: boolean,
  onBytes: (loaded: number) => void,
): Promise<SentResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = withCredentials;
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onBytes(e.loaded);
    };
    xhr.onload = () =>
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        text: xhr.responseText,
      });
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(body);
  });
}

function sentDetail(res: SentResponse): unknown {
  try {
    return JSON.parse(res.text);
  } catch {
    return res.text || res.statusText;
  }
}

function fileContentType(file: File): string {
  const t = (file.type || "").trim();
  return t || "application/octet-stream";
}

async function throwUpload(res: Response, label = "upload"): Promise<never> {
  let detail: unknown = res.statusText;
  try {
    detail = await res.json();
  } catch {
    /* ignore */
  }
  throw new Error(`${label} ${res.status}: ${JSON.stringify(detail)}`);
}

/** Legacy engine without /presign: one multipart POST /v1/uploads. */
async function uploadFilesMultipart(files: File[], onBytes?: (loaded: number) => void): Promise<UploadRecord[]> {
  const form = new FormData();
  for (const f of files) {
    form.append("files", f, f.name);
  }
  if (onBytes) {
    const sent = await sendWithProgress(
      "POST",
      `${baseUrl()}/v1/uploads`,
      form,
      engineAuthHeaders() as Record<string, string>,
      true,
      onBytes,
    );
    if (sent.status === 401) handleUnauthorizedSession();
    if (!sent.ok) throw new Error(`upload ${sent.status}: ${JSON.stringify(sentDetail(sent))}`);
    return ((JSON.parse(sent.text) as { uploads?: UploadRecord[] }).uploads || []);
  }
  const res = await engineFetch(`${baseUrl()}/v1/uploads`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    await throwUpload(res);
  }
  const data = (await res.json()) as { uploads?: UploadRecord[] };
  return data.uploads || [];
}

async function completeUpload(uploadId: string): Promise<UploadRecord> {
  const res = await engineFetch(
    `${baseUrl()}/v1/uploads/${encodeURIComponent(uploadId)}/complete`,
    { method: "POST" },
  );
  if (!res.ok) {
    await throwUpload(res);
  }
  const rec = (await res.json()) as UploadRecord;
  if (!rec?.upload_id) {
    throw new Error("upload complete missing upload_id");
  }
  return rec;
}

async function putPresigned(
  file: File,
  presign: PresignUploadResponse,
  onBytes?: (loaded: number) => void,
): Promise<void> {
  if (!presign.url) {
    throw new Error("upload presign missing url");
  }
  let putRes: Response | SentResponse;
  try {
    putRes = onBytes
      ? await sendWithProgress(presign.method || "PUT", presign.url, file, presign.headers || {}, false, onBytes)
      : await fetch(presign.url, {
          method: presign.method || "PUT",
          headers: presign.headers || {},
          body: file,
          credentials: "omit",
        });
  } catch (e) {
    const host = (() => {
      try {
        return new URL(presign.url).host;
      } catch {
        return "MinIO";
      }
    })();
    throw new Error(
      `MinIO is not reachable at ${host} (is it running?). ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  if (!putRes.ok) {
    let detail: unknown = putRes.statusText;
    try {
      detail = "text" in putRes && typeof putRes.text === "string" ? putRes.text : await (putRes as Response).text();
    } catch {
      /* ignore */
    }
    throw new Error(`upload PUT ${putRes.status}: ${JSON.stringify(detail)}`);
  }
}

async function uploadOnePresigned(
  file: File,
  presign: PresignUploadResponse,
  onBytes?: (loaded: number) => void,
): Promise<UploadRecord> {
  if (!presign.upload_id) {
    throw new Error("upload presign missing upload_id");
  }
  await putPresigned(file, presign, onBytes);
  return completeUpload(presign.upload_id);
}

/**
 * Prefer POST /v1/uploads/presign → raw PUT (MinIO, no engineFetch) → complete.
 * Multipart fallback: presign 404 (old engine), or a URL the browser cannot
 * PUT (http://minio:9000 on https://dev.app). 503 still does not fall back —
 * that means MinIO is down, not unreachable from the page.
 */
async function uploadFiles(files: File[], onProgress?: UploadProgress): Promise<UploadRecord[]> {
  const out: UploadRecord[] = [];
  // Progress is bytes over the whole batch; files go one after another.
  const total = Math.max(1, files.reduce((sum, f) => sum + f.size, 0));
  let done = 0;
  const bytesFrom = (start: number, cap: number) =>
    onProgress ? (loaded: number) => onProgress(Math.min(1, (start + Math.min(loaded, cap)) / total)) : undefined;
  const rest = (i: number) => files.slice(i).reduce((sum, f) => sum + f.size, 0);
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const res = await engineFetch(`${baseUrl()}/v1/uploads/presign`, {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        content_type: fileContentType(file),
      }),
    });
    if (res.status === 404) {
      out.push(...(await uploadFilesMultipart(files.slice(i), bytesFrom(done, rest(i)))));
      onProgress?.(1);
      return out;
    }
    if (!res.ok) {
      await throwUpload(res);
    }
    const presign = (await res.json()) as PresignUploadResponse;
    if (!presignUsableInBrowser(presign.url || "")) {
      logger.info("upload_presign_not_browser_reachable", {
        host: (() => {
          try {
            return new URL(presign.url).host;
          } catch {
            return "";
          }
        })(),
      });
      out.push(...(await uploadFilesMultipart(files.slice(i), bytesFrom(done, rest(i)))));
      onProgress?.(1);
      return out;
    }
    out.push(await uploadOnePresigned(file, presign, bytesFrom(done, file.size)));
    done += file.size;
    onProgress?.(done / total);
  }
  return out;
}

export function createJob(body: JobCreateRequest): Promise<JobRecord> {
  return jsonFetch<JobRecord>("/v1/jobs", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Org-scoped job history (metadata only; use getJob to rehydrate full results). */
export interface JobListItem {
  job_id: string;
  tenant?: string;
  organization_slug?: string;
  file_name?: string | null;
  drawing_id?: string | null;
  status?: string;
  created_at?: string | null;
  updated_at?: string | null;
  /**
   * Line count when the engine measured one. `GET /v1/jobs` currently
   * setdefaults this to 0 without reading bom_items — see `bomItemCount`.
   */
  items?: number;
  /** True when engine baked a whole-drawing PNG at parse. */
  has_thumbnail?: boolean;
  /** Relative URL e.g. /v1/jobs/{id}/artifacts/thumbnail */
  thumbnail_url?: string | null;
}

export function listJobs(limit = 30): Promise<JobListItem[]> {
  return jsonFetch<JobListItem[]>(`/v1/jobs?limit=${encodeURIComponent(String(limit))}`);
}

export function getJob(jobId: string): Promise<JobRecord> {
  return jsonFetch<JobRecord>(`/v1/jobs/${encodeURIComponent(jobId)}`);
}

/** Permanently delete a job (list + store + thumbnail). */
export function deleteJob(jobId: string): Promise<{ deleted: boolean; job_id: string }> {
  return jsonFetch<{ deleted: boolean; job_id: string }>(
    `/v1/jobs/${encodeURIComponent(jobId)}`,
    { method: "DELETE" },
  );
}

/**
 * Download the original master CAD file (STEP/DXF/DWG) for a job.
 * Triggers a browser save dialog.
 */
export async function downloadJobSource(
  jobId: string,
  fallbackName = "drawing.bin",
): Promise<void> {
  const res = await engineFetch(
    `${baseUrl()}/v1/jobs/${encodeURIComponent(jobId)}/source`,
  );
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* ignore */
    }
    throw new Error(`download source ${res.status}: ${detail}`);
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
  let filename = fallbackName;
  if (match?.[1]) {
    try {
      filename = decodeURIComponent(match[1].replace(/"/g, "").trim());
    } catch {
      filename = match[1].replace(/"/g, "").trim() || fallbackName;
    }
  }
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export function parseJob(jobId: string, body: JobParseRequest = {}): Promise<JobRecord> {
  return jsonFetch<JobRecord>(`/v1/jobs/${encodeURIComponent(jobId)}/parse`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type HitlItemEdit = {
  item_no: number;
  material?: string;
  qty?: number;
};

export function approveHitl(
  jobId: string,
  body: {
    node_ids?: string[];
    item_nos?: number[];
    approve_all?: boolean;
    edits?: HitlItemEdit[];
  } = {},
): Promise<JobRecord> {
  return jsonFetch<JobRecord>(`/v1/jobs/${encodeURIComponent(jobId)}/hitl/approve`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function rejectHitl(
  jobId: string,
  body: {
    node_ids?: string[];
    item_nos?: number[];
    reject_all?: boolean;
    no_bid?: boolean;
    reason: string;
  },
): Promise<JobRecord> {
  return jsonFetch<JobRecord>(`/v1/jobs/${encodeURIComponent(jobId)}/hitl/reject`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Export commercial quote PDF/XLS when unlocked (HITL).
 * Cloud: format keys are relative URLs (/v1/jobs/.../artifacts/pdf).
 * Desktop local: may still be host paths; also returns local_paths.
 */
export function exportJobQuote(
  jobId: string,
  body: { output_dir?: string; formats?: string[] } = {},
): Promise<Record<string, unknown>> {
  return jsonFetch<Record<string, unknown>>(`/v1/jobs/${encodeURIComponent(jobId)}/quote`, {
    method: "POST",
    body: JSON.stringify({
      formats: body.formats ?? ["pdf", "excel", "json"],
      output_dir: body.output_dir,
    }),
  });
}

export type DrawingExportScope = "assembly" | "part";

/**
 * Pre-quote üretim çizimi (shape + data multi-view PDF). Not gated by HITL.
 * - assembly: whole job drawing
 * - part: one BOM solid (solid_index or item_no)
 */
export function exportJobDrawing(
  jobId: string,
  body: {
    scope?: DrawingExportScope;
    solid_index?: number | null;
    item_no?: number | null;
    output_dir?: string;
  } = {},
): Promise<Record<string, unknown>> {
  const payload: Record<string, unknown> = {
    scope: body.scope ?? "assembly",
  };
  if (body.solid_index != null) payload.solid_index = body.solid_index;
  if (body.item_no != null) payload.item_no = body.item_no;
  if (body.output_dir) payload.output_dir = body.output_dir;
  return jsonFetch<Record<string, unknown>>(
    `/v1/jobs/${encodeURIComponent(jobId)}/drawings`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

/** Pick a downloadable URL from drawing export response. */
export function drawingExportDownload(
  written: Record<string, unknown>,
): { url: string; kind: string } | null {
  if (typeof written.drawing_pdf === "string" && written.drawing_pdf) {
    return { url: written.drawing_pdf, kind: "drawing_pdf" };
  }
  for (const [k, v] of Object.entries(written)) {
    if (k.startsWith("drawing_pdf_part_") && typeof v === "string" && v) {
      return { url: v, kind: k };
    }
  }
  const local = written.local_paths;
  if (local && typeof local === "object") {
    const lp = local as Record<string, unknown>;
    if (typeof lp.drawing_pdf === "string") {
      return { url: lp.drawing_pdf, kind: "drawing_pdf" };
    }
    for (const [k, v] of Object.entries(lp)) {
      if (k.startsWith("drawing_pdf_part_") && typeof v === "string") {
        return { url: v, kind: k };
      }
    }
  }
  return null;
}

/** True when Quote CTA must stay disabled. */
export function isQuoteBlocked(job: JobRecord | null | undefined): boolean {
  if (!job) return true;
  if (job.quote_blocked) return true;
  if (job.status !== "ready" && job.status !== "partial") return true;
  return (job.quote_blockers?.length ?? 0) > 0;
}

export type QuoteBlockKey =
  | "quote.download"
  | "quote.hitl"
  | "quote.partial"
  | "quote.parse"
  | "quote.geometry"
  | "quote.substitute"
  | "quote.noBid"
  | "quote.locked";

export function quoteBlockKey(job: JobRecord | null | undefined): QuoteBlockKey {
  if (!job || !isQuoteBlocked(job)) return "quote.download";
  const blockers = job.quote_blockers || [];
  if (blockers.includes("no_bid") || job.no_bid) return "quote.noBid";
  if (blockers.some((b) => b.startsWith("hitl_open:"))) return "quote.hitl";
  if (blockers.some((b) => b.startsWith("partial_files"))) return "quote.partial";
  if (blockers.includes("not_parsed") || blockers.includes("not_ready")) return "quote.parse";
  if (blockers.includes("geometry_uncertain")) return "quote.geometry";
  if (blockers.includes("substitute_needs_ok")) return "quote.substitute";
  return "quote.locked";
}
